import React from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import { MapPin, Phone, Home } from "react-icons/fa6"; // React icons
import { FaHome, FaMapMarkerAlt, FaPhone } from "react-icons/fa";
import { useRoForm } from "./RoContext.js";

const steps = [
    { label: "Personal Info", link: "/personalinformationmanagerhub" },
    { label: "Residency Address", link: "/residencyaddmanager" },
    { label: "KYC", link: "/kycmanagerhub" },
    { label: "Bank Details", link: "/bankaccountdetailsmanagerhub" },
    { label: "Agreement", link: "/aggrementdetailsmanager" },
];

const validationSchema = Yup.object({
    residentialAddress: Yup.string().required("Residential Address Required"),
    residentialPincode: Yup.string()
        .matches(/^\d{6}$/, "Must be 6 digits")
        .required("Residential Pincode Required"),
    residentialCity: Yup.string().required("Residential City Required"),
    residentialState: Yup.string().required("Residential State Required"),
    residentialTelephoneNo: Yup.string()
        .matches(/^[0-9]{10}$/, "Must be 10 digits")
        .required("Contact NO. Required"),
});

const ResidencyAddManager = ({ currentStep = 2 }) => {
    const { formData, updateFormData, completedSteps, setCompletedSteps } = useRoForm();

    const navigate = useNavigate();

    const blankForm = {
        residentialAddress: "",
        residentialPincode: "",
        residentialCity: "",
        residentialState: "",
        residentialTelephoneNo: ""
    };
    const initialValues = { ...blankForm, ...formData };
    const handleStepClick = (link) => {
        navigate(link);
    };

    const handleSubmit = (values) => {
        if (!completedSteps.includes('/residencyaddmanager')) {
            setCompletedSteps([...completedSteps, '/residencyaddmanager']);
        }
        updateFormData(values, '/residencyaddmanager'); // e.g., '/kycverification'


        // navigate to next step in wizard (example)
        navigate("/kycmanagerhub");
        // Replace navigate with your API call to save data
        // console.log("Form values:", values);
    };

    return (
        <div className="px-4 py-6 bg-white rounded-lg mb-10 grid grid-cols-1">
            <h1 className="font-bold text-gray-800 mb-7">
                Step {currentStep}: Residency Address
            </h1>

            {/* Steps Navigation */}
            <div className="flex justify-between pb-6 w-full overflow-x-auto whitespace-nowrap cursor-pointer items-center relative mb-4 scrollbar-hide">
                {steps.map((step, index) => (
                    <div
                        key={index}
                        className="flex-1 text-white flex flex-col items-center flex-shrink-0 min-w-[140px] relative"
                    >
                        {index !== 0 && (
                            <div className="absolute top-5 left-[-50%] w-[100%] h-0.5 bg-gray-300 z-0" />
                        )}
                        <div className="z-10" onClick={() => handleStepClick(step.link)}>
                            <div
                                className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold 
                  ${index + 1 === currentStep
                                        ? "bg-slate-900 text-white shadow-md"
                                        : "bg-white text-gray-500 border border-gray-300"
                                    }`}
                            >
                                {index + 1}
                            </div>
                        </div>
                        <span className="mt-2 text-sm text-center text-gray-700">
                            {step.label}
                        </span>
                    </div>
                ))}
            </div>

            {/* Form */}
            <div className="bg-white md:border md:p-6 rounded-lg md:shadow-sm">
                <h2 className="text-lg font-bold mb-6 text-black">
                    Address Details (Residential)
                </h2>

                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ errors, touched }) => (
                        <Form className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {[
                                {
                                    name: "residentialAddress",
                                    label: "Residential Address",
                                    placeholder: "Enter full address",
                                    icon: <FaHome className="text-gray-400" />,
                                },
                                {
                                    name: "residentialPincode",
                                    label: "Residential Pincode",
                                    placeholder: "Enter pincode",
                                    icon: <FaMapMarkerAlt className="text-gray-400" />,
                                },
                                {
                                    name: "residentialCity",
                                    label: "Residential City",
                                    placeholder: "Enter city",
                                    icon: <FaMapMarkerAlt className="text-gray-400" />,
                                },
                                {
                                    name: "residentialState",
                                    label: "Residential State",
                                    placeholder: "Enter state",
                                    icon: <FaMapMarkerAlt className="text-gray-400" />,
                                },
                                {
                                    name: "residentialTelephoneNo",
                                    label: "Contact No.",
                                    placeholder: "Enter Contact No.",
                                    icon: <FaPhone className="text-gray-400" />,
                                },
                            ].map(({ name, label, placeholder, icon }) => (
                                <div key={name} className="w-full">
                                    <label
                                        htmlFor={name}
                                        className="block text-sm font-medium text-slate-700 mb-2"
                                    >
                                        {label} <span className="text-red-600">*</span>
                                    </label>
                                    <div className="relative">
                                        <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                                            {icon}
                                        </span>
                                        <Field
                                            id={name}
                                            name={name}
                                            type="text"
                                            placeholder={placeholder}
                                            className={`w-full pl-10 pr-4 py-2 rounded-md bg-blue-50 placeholder-gray-400 
                        focus:outline-none focus:ring-2 focus:ring-slate-900 border 
                        ${errors[name] && touched[name]
                                                    ? "border-red-500"
                                                    : "border-gray-300"
                                                }`}
                                        />
                                    </div>
                                    <ErrorMessage
                                        name={name}
                                        component="div"
                                        className="text-red-500 text-xs mt-1"
                                    />
                                </div>
                            ))}

                            {/* Submit Button */}
                            <div className="col-span-1 md:col-span-3 flex justify-end mt-4">
                                <button
                                    type="submit"
                                    className="bg-slate-900 hover:bg-slate-800 text-white px-6 py-2 rounded-lg font-semibold shadow-md transition"
                                >
                                    Next
                                </button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </div>
    );
};

export default ResidencyAddManager;

function Page() {
    return <ResidencyAddManager currentStep={2} />;
}
